/// <amd-module name="Wiki/Overview/Stores/StoresHub" />
import { Action } from "VSS/Reflux/Action";
import { IEditorStore } from "Wiki/Editor/Contracts";
import { IHeaderMetaDataStore } from "Wiki/HeaderMetaData/Interfaces/IHeaderMetadataTypes";
import * as ACPayload from "Wiki/Overview/Actions/ActionCreatorPayloads";
import { IOverviewState, OverviewStore } from "Wiki/Overview/Stores/OverviewStore";
import { PageHeirarchyFetchedPayload, PageToggledPayload, WikiTreeState, WikiTreeStore } from "Wiki/Overview/Stores/WikiTreeStore";
import { FilterResultsUpdatedPayload, FilterTreeState, FilterTreeStore } from "Wiki/PageTree/Stores/FilterTreeStore";
import { ActionListener } from "Wiki/ViewCommon/ActionListener";
import { ErrorState, OperationStatus, WikiPageInfo } from "Wiki/ViewCommon/Contracts";
import { getFileName } from "Wiki/ViewCommon/Utils/PathHelper";

export class OverviewActions {
    public editorModuleLoaded = new Action<void>();
    public startEditing = new Action<WikiPageInfo>();
    public startEditingNewPage = new Action<ACPayload.NewPageEditPayload>();
    public startEditingCurrentPage = new Action<void>();
    public closeEditor = new Action<void>();
    public contentTranslatedForEditor = new Action<string>();
    public anchorClicked = new Action<string>();
    public anchorRemoved = new Action<void>();

    public linkedWitUpdated = new Action<number>();

    public allPagesFetched = new Action<ACPayload.WikiPagesFetchedPayload>();
    // this action is different from allpagesFetched or pageFetched, since we know the parent and can have some optimization around it.
    public childPagesFetched = new Action<PageHeirarchyFetchedPayload>();
    public pageSelected = new Action<ACPayload.PageSelectedPayload>();
    public pageInfoLoaded = new Action<ACPayload.PageInfoLoadedPayload>();
    public onPageMoveCompleted = new Action<ACPayload.PageMovedPayload>();
    public treeItemToggled = new Action<PageToggledPayload>();
    public pageSaved = new Action<ACPayload.PageSavedPayload>();
    public pageDeleted = new Action<ACPayload.PageDeletedPayload>();
    public pageReordered = new Action<ACPayload.PageReorderedPayload>();
    public pageHierarchyReloaded = new Action<ACPayload.PageHierarchyReloadedPayload>();
    public pageReorderStarted = new Action<void>();
    public partialPagetreeLoaded = new Action<ACPayload.WikiPagesFetchedPayload>();

    // nav pane
    public navPaneResized = new Action<number>();
    public navPaneVisibilityChanged = new Action<boolean>();

    // filter
    public onFilteringComplete = new Action<ACPayload.FilteringCompletedPayload>();
    public onFilterTextUpdated = new Action<string>();
    public onFilterResultsUpdated = new Action<FilterResultsUpdatedPayload>();

    // follow page related
    public unsetUnfollowPage = new Action<void>();
    public refreshFollowStatus = new Action<boolean>();

    // error
    public onErrorChanged = new Action<string | void>();
    public onEmbedWitFailed = new Action<ErrorState>();
}

export interface Stores {
    overviewStore: OverviewStore;
    treeStore: WikiTreeStore;
    filterStore: FilterTreeStore;
}

export interface State {
    overviewState: IOverviewState;
    treeState: WikiTreeState;
    filterState: FilterTreeState;
}

export class StoresHub {
    private _actionListener: ActionListener;
    private _asyncEditorStoreActionsListenerRemover: () => void | undefined;
    private _asyncHeaderMetaDataStoreActionsListenerRemover: () => void | undefined;
    public asyncEditorStore: IEditorStore | undefined;
    public asyncHeaderMetaDataStore: IHeaderMetaDataStore | undefined;

    constructor(public stores: Stores, private _overviewActions: OverviewActions) {
        this._actionListener = new ActionListener();

        this._actionListener.addListener(this._overviewActions.editorModuleLoaded, stores.overviewStore.editorModuleLoaded);

        this._actionListener.addListener(this._overviewActions.startEditing, (payload: WikiPageInfo) => {
            stores.overviewStore.startEditing(payload);
            this.asyncEditorStore && this.asyncEditorStore.resetState(getFileName(payload.path));
        });

        this._actionListener.addListener(this._overviewActions.startEditingNewPage, (payload: ACPayload.NewPageEditPayload) => {
            // new page name should be empty, we are forcing it here because, the payload is also used in editor state reset, but the editor state might need a initial page title
            stores.overviewStore.startEditingNewPage({ ...payload.newPageInfo, pageName: "" });
            this.asyncEditorStore && this.asyncEditorStore.resetState(payload.newPageInfo.pageName, payload.zeroDay);
        });

        this._actionListener.addListener(this._overviewActions.startEditingCurrentPage, () => {
            stores.overviewStore.startEditingCurrentPage();
            this._resetEditorState();
        });

        this._actionListener.addListener(this._overviewActions.contentTranslatedForEditor, (content: string) => {
            this.asyncEditorStore && this.asyncEditorStore.initializeTranslatedContent(content);
        });

        this._actionListener.addListener(this._overviewActions.anchorClicked, stores.overviewStore.onAnchorClicked);
        this._actionListener.addListener(this._overviewActions.anchorRemoved, stores.overviewStore.onAnchorRemoved);
        this._actionListener.addListener(this._overviewActions.closeEditor, () => {
            stores.overviewStore.closeEditor();
            this._resetEditorState();
        });

        this._actionListener.addListener(this._overviewActions.linkedWitUpdated, payload => {
            this.asyncHeaderMetaDataStore && this.asyncHeaderMetaDataStore.onLinkedWorkItemsUpdated(payload);
        });

        this._actionListener.addListener(this._overviewActions.pageSaved, payload => {
            stores.overviewStore.savePage(payload);
            this.asyncEditorStore && this.asyncEditorStore.updateSavePageStatus(OperationStatus.Completed);
        });

        this._actionListener.addListener(this._overviewActions.pageInfoLoaded, stores.overviewStore.onPageInfoLoaded);

        this._actionListener.addListener(this._overviewActions.childPagesFetched, stores.treeStore.childPagesFetched);
        // TODO: place holder to update tree on page move, will be worked on subsequent PRs
        // this._actionListener.addListener(this._overviewActions.pageMoved, stores.treeStore.onPageMoved);
        this._actionListener.addListener(this._overviewActions.treeItemToggled, stores.treeStore.treeItemToggled);
        this._actionListener.addListener(this._overviewActions.allPagesFetched, stores.treeStore.loadAllPages);
        this._actionListener.addListener(this._overviewActions.pageDeleted, stores.treeStore.pageDeleted);
        this._actionListener.addListener(this._overviewActions.pageReorderStarted, stores.treeStore.onPageReorderStarted);
        this._actionListener.addListener(this._overviewActions.pageReordered, stores.treeStore.onPageReordered);
        this._actionListener.addListener(this._overviewActions.pageReordered, stores.overviewStore.onPageReordered);
        this._actionListener.addListener(this._overviewActions.onPageMoveCompleted, stores.treeStore.onPageMoved);
        this._actionListener.addListener(this._overviewActions.pageHierarchyReloaded, stores.treeStore.onPageHierarchyReloaded);
        this._actionListener.addListener(this._overviewActions.onFilteringComplete, stores.treeStore.onFilteringComplete);
        this._actionListener.addListener(this._overviewActions.pageSaved, stores.treeStore.pageSaved);

        this._actionListener.addListener(this._overviewActions.onFilterTextUpdated, stores.filterStore.onFilterTextChanged);
        this._actionListener.addListener(this._overviewActions.onFilteringComplete, stores.filterStore.onFilteringComplete);
        this._actionListener.addListener(this._overviewActions.onFilterResultsUpdated, stores.filterStore.onFilterResultsUpdated);
        this._actionListener.addListener(this._overviewActions.pageSelected, payload => {
            stores.overviewStore.onPageSelected(payload.wikiPage, payload.anchor, payload.isSystemAction);
        });
        this._actionListener.addListener(this._overviewActions.partialPagetreeLoaded, stores.treeStore.loadPartialPages);

        this._actionListener.addListener(this._overviewActions.navPaneResized, stores.overviewStore.updateNavPaneSize);
        this._actionListener.addListener(this._overviewActions.navPaneVisibilityChanged, stores.overviewStore.updateNavPaneVisibility);

        this._actionListener.addListener(this._overviewActions.unsetUnfollowPage, stores.overviewStore.onUnsetUnfollowPage);
        this._actionListener.addListener(this._overviewActions.refreshFollowStatus, stores.overviewStore.onRefreshFollowStatus);

        this._actionListener.addListener(this._overviewActions.onEmbedWitFailed, stores.overviewStore.updateError);
        this._actionListener.addListener(this._overviewActions.onErrorChanged, payload => {
            stores.overviewStore.updateError({ message: payload || undefined });
        });
        this._actionListener.addListener(this._overviewActions.onErrorChanged, stores.treeStore.onError);
    }

    public registerEditorStore(editorStore: IEditorStore, actionListenerRemover: () => void): void {
        if (this.asyncEditorStore == null) {
            this.asyncEditorStore = editorStore;
            this._asyncEditorStoreActionsListenerRemover = actionListenerRemover;
        }
    }

    public registerHeaderMetaDataStore(headerMetaDataStore: IHeaderMetaDataStore, actionListenerRemover: () => void): void {
        if (this.asyncHeaderMetaDataStore == undefined) {
            this.asyncHeaderMetaDataStore = headerMetaDataStore;
            this._asyncHeaderMetaDataStoreActionsListenerRemover = actionListenerRemover;
        }
    }

    public removeActionListeners = (): void => {
        this._actionListener.removeListeners();
        this._asyncEditorStoreActionsListenerRemover && this._asyncEditorStoreActionsListenerRemover();
        this._asyncHeaderMetaDataStoreActionsListenerRemover && this._asyncHeaderMetaDataStoreActionsListenerRemover();
    };

    public getState = (): State => {
        return {
            treeState: this.stores.treeStore.getState(),
            overviewState: this.stores.overviewStore.getState(),
            filterState: this.stores.filterStore.getState()
        };
    };

    private _resetEditorState = (): void => {
        const pageInfo = this.stores.overviewStore.getState().pageInfo;
        // one possible scenario where pageInfo would be null is on broken page link experience
        // though the above scenario would not result in this code flow, we are checking to make it compilable

        let pageName: string | undefined;
        if (pageInfo) {
            pageName = getFileName(pageInfo.path);
        } else {
            if ($DEBUG) {
                throw new Error("cannot call edit on broken page");
            }
        }
        this.asyncEditorStore && pageName !== undefined && this.asyncEditorStore.resetState(pageName);
    };
}
